/**
 * Availability Calendar Component
 *
 * Visual calendar that shows container availability and delivery capacity
 * for each date, helping customers choose the best delivery date.
 */

import { useState, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isBefore, isAfter, addMonths, subMonths } from 'date-fns';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon, BoltIcon } from 'lucide-react';
import { Badge } from '../ui/badge';

export type AvailabilityStatus = 'available' | 'limited' | 'unavailable';

export interface DayAvailability {
  date: Date;
  status: AvailabilityStatus;
  slots?: number;
  containers?: number;
  isASAP?: boolean;
}

interface AvailabilityCalendarProps {
  minDate: Date;
  maxDate: Date;
  selectedDate: Date | null;
  availability: Map<string, DayAvailability>;
  onDateSelect: (date: Date, isASAP?: boolean) => void;
  showASAP?: boolean;
  isLoading?: boolean;
}

export function AvailabilityCalendar({
  minDate,
  maxDate,
  selectedDate,
  availability,
  onDateSelect,
  showASAP = true,
  isLoading = false,
}: AvailabilityCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Find next available date for ASAP
  const nextAvailableDate = useMemo(() => {
    const dates = Array.from(availability.values())
      .filter(d => d.status === 'available')
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    return dates[0]?.date || null;
  }, [availability]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const handlePreviousMonth = () => {
    const prevMonth = subMonths(currentMonth, 1);
    if (!isBefore(endOfMonth(prevMonth), minDate)) {
      setCurrentMonth(prevMonth);
    }
  };

  const handleNextMonth = () => {
    const nextMonth = addMonths(currentMonth, 1);
    if (!isAfter(startOfMonth(nextMonth), maxDate)) {
      setCurrentMonth(nextMonth);
    }
  };

  const getDayAvailability = (date: Date): DayAvailability | undefined => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return availability.get(dateKey);
  };

  const getDayStatus = (date: Date): AvailabilityStatus => {
    const dayAvailability = getDayAvailability(date);
    return dayAvailability?.status || 'unavailable';
  };

  const isDateSelectable = (date: Date): boolean => {
    if (isBefore(date, minDate) || isAfter(date, maxDate)) return false;
    const status = getDayStatus(date);
    return status !== 'unavailable';
  };

  const getDayClasses = (date: Date): string => {
    const status = getDayStatus(date);
    const isSelected = selectedDate && isSameDay(date, selectedDate);
    const isSelectable = isDateSelectable(date);
    const isNextAvailable = nextAvailableDate && isSameDay(date, nextAvailableDate);

    return cn(
      'relative h-12 w-12 rounded-lg font-medium transition-all',
      'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
      {
        // Base states
        'hover:bg-gray-100 cursor-pointer': isSelectable && !isSelected,
        'cursor-not-allowed opacity-40': !isSelectable,

        // Availability states
        'bg-green-50 text-green-900 hover:bg-green-100': status === 'available' && !isSelected,
        'bg-yellow-50 text-yellow-900 hover:bg-yellow-100': status === 'limited' && !isSelected,
        'bg-gray-100 text-gray-400': status === 'unavailable',

        // Selected state
        'bg-primary-500 text-white hover:bg-primary-600': !!isSelected,

        // ASAP indicator
        'ring-2 ring-green-500 ring-offset-2': !!(isNextAvailable && !isSelected),
      }
    );
  };

  return (
    <div className="space-y-4">
      {/* ASAP Option */}
      {showASAP && nextAvailableDate && (
        <Card
          className={cn(
            'cursor-pointer transition-all',
            'hover:shadow-md',
            selectedDate && getDayAvailability(selectedDate)?.isASAP
              ? 'ring-2 ring-primary shadow-lg'
              : ''
          )}
          onClick={() => onDateSelect(nextAvailableDate, true)}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <BoltIcon className="w-6 h-6 text-yellow-500" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    ASAP - Next Available Delivery
                  </p>
                  <p className="text-sm text-gray-600">
                    {format(nextAvailableDate, 'EEEE, MMMM d, yyyy')}
                  </p>
                </div>
              </div>
              <Badge variant="default">Fastest Option</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar */}
      <Card>
        <CardContent className="p-4">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handlePreviousMonth}
              disabled={isBefore(endOfMonth(subMonths(currentMonth, 1)), minDate)}
              className="p-2"
            >
              <ChevronLeftIcon className="w-5 h-5" />
              <span className="sr-only">Previous month</span>
            </Button>

            <h3 className="text-lg font-semibold text-gray-900">
              {format(currentMonth, 'MMMM yyyy')}
            </h3>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleNextMonth}
              disabled={isAfter(startOfMonth(addMonths(currentMonth, 1)), maxDate)}
              className="p-2"
            >
              <ChevronRightIcon className="w-5 h-5" />
              <span className="sr-only">Next month</span>
            </Button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {/* Day headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div
                key={day}
                className="h-8 flex items-center justify-center text-xs font-medium text-gray-600"
              >
                {day}
              </div>
            ))}

            {/* Empty cells for alignment */}
            {monthDays.length > 0 && Array.from({ length: monthDays[0]?.getDay() ?? 0 }).map((_, index) => (
              <div key={`empty-${index}`} className="h-12" />
            ))}

            {/* Day cells */}
            {monthDays.map((date) => {
              const dayAvailability = getDayAvailability(date);
              const isSelectable = isDateSelectable(date);

              return (
                <button
                  key={format(date, 'yyyy-MM-dd')}
                  type="button"
                  disabled={!isSelectable || isLoading}
                  onClick={() => isSelectable && onDateSelect(date)}
                  className={getDayClasses(date)}
                  aria-label={`${format(date, 'MMMM d, yyyy')}. ${
                    dayAvailability?.status === 'available'
                      ? 'Available'
                      : dayAvailability?.status === 'limited'
                      ? 'Limited availability'
                      : 'Unavailable'
                  }`}
                >
                  <span className="text-sm">{format(date, 'd')}</span>
                  {dayAvailability?.status === 'limited' && (
                    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
                      <div className="w-1 h-1 bg-yellow-600 rounded-full" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-100 rounded" />
              <span className="text-gray-600">Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-100 rounded" />
              <span className="text-gray-600">Limited</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-100 rounded" />
              <span className="text-gray-600">Unavailable</span>
            </div>
          </div>

          {/* Selected Date Display */}
          {selectedDate && (
            <div className="mt-4 p-3 bg-primary-50 rounded-lg">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-primary" />
                <p className="text-sm font-medium text-primary-900">
                  Selected: {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                </p>
              </div>
              {getDayAvailability(selectedDate)?.containers && (
                <p className="text-xs text-primary-700 mt-1">
                  {getDayAvailability(selectedDate)?.containers} containers available
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}